import { Link } from "react-router-dom";
import { Radar, TrendingUp, AlertTriangle, UserCircle2, MapPin, ArrowRight } from "lucide-react";
import { SectionHead, Reveal } from "@/components/site/Section";
import { useSiteContent, fileUrl } from "@/context/SiteContentContext";

const Tag = ({ c, children }) => <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: c }}>{children}</span>;

const IntelSample = () => (
  <div className="card-box p-6 h-full" data-testid="sample-intel">
    <div className="flex items-center gap-2"><Radar className="h-5 w-5 text-[#06b6d4]" /><h3 className="font-semibold">Market Intelligence report</h3></div>
    <p className="mt-1 text-xs text-muted-foreground">Week 24 · FireShield Safety Systems · Fire safety manufacturer</p>
    <div className="mt-5 space-y-3 text-sm">
      <div className="rounded-xl bg-foreground/5 p-3"><Tag c="#06b6d4">Competitor change</Tag><div className="mt-1">A rival extinguisher brand launched an "Annual Refill & Inspection" subscription page.</div></div>
      <div className="rounded-xl bg-[#10b981]/10 p-3"><Tag c="#10b981">Opportunity · high</Tag><div className="mt-1 flex gap-2"><TrendingUp className="h-4 w-4 shrink-0 text-[#10b981]" />New fire NOC renewal rules for warehouses — promote audit-ready detection systems.</div></div>
      <div className="rounded-xl bg-red-500/10 p-3"><Tag c="#ef4444">Threat · medium</Tag><div className="mt-1 flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0 text-red-500" />Low-cost imported smoke detectors undercutting prices by 20% on marketplaces.</div></div>
    </div>
    <div className="mt-4 text-xs text-muted-foreground">3 recommendations · 2 accepted</div>
  </div>
);

const PERSONAS = [["Facility Manager", "35–50, owns compliance for offices & malls, renews AMCs yearly", "#7c3aed", 84], ["Fire Safety Consultant", "30–55, specifies systems for new buildings, needs datasheets", "#db2777", 68], ["Warehouse Ops Head", "38–52, needs NOC-ready detection & suppression fast", "#10b981", 76]];
const PersonaSample = () => (
  <div className="card-box p-6 h-full" data-testid="sample-personas">
    <div className="flex items-center gap-2"><UserCircle2 className="h-5 w-5 text-[#7c3aed]" /><h3 className="font-semibold">Customer Personas</h3></div>
    <p className="mt-1 text-xs text-muted-foreground">Built from 1,240 CRM records + 86 dealer leads</p>
    <div className="mt-5 space-y-3">{PERSONAS.map(([n, d, c, s]) => (
      <div key={n} className="rounded-xl border p-3">
        <div className="flex items-center justify-between"><span className="text-sm font-semibold">{n}</span><span className="text-[11px] font-semibold" style={{ color: c }}>Intent {s}</span></div>
        <div className="text-xs text-muted-foreground mt-1">{d}</div>
        <div className="mt-2 h-1.5 rounded-full bg-foreground/10"><div className="h-full rounded-full" style={{ width: `${s}%`, background: c }} /></div>
      </div>))}</div>
  </div>
);

const EV = { 3: ["#4f46e5", "Fire India Expo"], 9: ["#f59e0b", "Safety & Security Summit"], 10: ["#f59e0b", ""], 17: ["#10b981", "Dealer Meet"], 24: ["#db2777", "Fire Safety Week"] };
const EventsSample = () => (
  <div className="card-box p-6 h-full" data-testid="sample-events">
    <div className="flex items-center gap-2"><MapPin className="h-5 w-5 text-[#f59e0b]" /><h3 className="font-semibold">Industry Events calendar</h3></div>
    <p className="mt-1 text-xs text-muted-foreground">Curated · yours · AI-suggested (verify)</p>
    <div className="mt-5 grid grid-cols-7 gap-1 text-[10px]">
      {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i} className="text-center text-muted-foreground font-mono">{d}</div>)}
      {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
        <div key={d} className="aspect-square rounded-md border p-0.5 relative">
          <span className="text-[9px]">{d}</span>
          {EV[d] && <div className="absolute inset-x-0.5 bottom-0.5 h-1.5 rounded-sm" style={{ background: EV[d][0] }} title={EV[d][1]} />}
        </div>))}
    </div>
    <ul className="mt-4 space-y-1.5 text-xs">{Object.entries(EV).filter(([, v]) => v[1]).map(([d, [c, n]]) => <li key={d} className="flex items-center gap-2"><i className="h-2 w-2 rounded-full" style={{ background: c }} />{d} · {n}</li>)}</ul>
  </div>
);

export const AgentSamples = () => (
  <section className="py-20 md:py-28" data-testid="agent-samples-section">
    <div className="container-x">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <SectionHead eyebrow="Samples · illustrative data" title="See What Your Intelligence Agents Deliver" copy="Market Intelligence, Customer Personas and an Industry Events calendar — working together to target the right events and write content that converts." />
        <Link to="/tour" className="btn-ghost shrink-0" data-testid="agent-samples-tour">Explore the demo <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-12 grid gap-5 lg:grid-cols-3">
        <Reveal><IntelSample /></Reveal>
        <Reveal delay={0.08}><PersonaSample /></Reveal>
        <Reveal delay={0.16}><EventsSample /></Reveal>
      </div>
    </div>
  </section>
);

const Row = ({ items, reverse }) => (
  <div className="flex overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
    <div className={`flex w-max gap-6 animate-marquee ${reverse ? "[animation-direction:reverse]" : ""}`}>
      {[...items, ...items, ...items].map((c, i) => (
        <a key={i} href={c.website || undefined} target="_blank" rel="noreferrer" className="flex h-20 w-44 shrink-0 items-center justify-center rounded-2xl border bg-white p-4">
          <img src={fileUrl(c.logo_url)} alt={c.name} className="max-h-12 max-w-full object-contain" loading="lazy" />
        </a>))}
    </div>
  </div>
);

export const ClientsMarquee = () => {
  const { content } = useSiteContent();
  const clients = content?.clients || [];
  if (!clients.length) return null;
  const half = Math.ceil(clients.length / 2);
  const r1 = clients.slice(0, half);
  const r2 = clients.length > 1 ? clients.slice(half) : clients;
  return (
    <section className="py-16 border-y bg-card/40" data-testid="clients-section">
      <div className="container-x"><div className="eyebrow mb-8">Our clients</div></div>
      <div className="space-y-5"><Row items={r1} /><Row items={r2.length ? r2 : r1} reverse /></div>
    </section>
  );
};

export const AnnouncementBar = () => {
  const { content } = useSiteContent();
  const a = content?.announcement;
  if (!a?.enabled || !a.text) return null;
  return (
    <div className="bg-brand text-white text-sm text-center px-4 py-2" data-testid="announcement-bar">
      {a.text} {a.link && <a href={a.link} className="underline font-semibold ml-1">Learn more</a>}
    </div>
  );
};
